import React, { useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { attendShort as shortOf, NIGHT_CODE } from '../../services/attendCodes';

const LONG_PRESS_DURATION = 400;
const LONG_PRESS_MOVE_THRESHOLD = 10; // px — если палец ушёл дальше, отменяем long-press

// Night work — персональная ночная смена: индиго, не красный как отсутствие.

function TimesheetCell({
    employeeId,
    jobId,
    employeeIndex,
    jobIndex,
    hours,
    editingValue,
    syncStatus,
    workflowStatus,
    rejectionReason,
    rejectedByName,
    hasNotes,
    attendCode,
    isCompact,
    cellSize,
    isActive,
    isReadOnly,
    isLastCol,
    onShortPress,
    onLongPress,
}) {
    const pressTimer = useRef(null);
    const isLongPress = useRef(false);
    const pressStartPos = useRef(null);

    const cancelTimer = useCallback(() => {
        if (pressTimer.current) {
            clearTimeout(pressTimer.current);
            pressTimer.current = null;
        }
    }, []);

    // Унифицированный pointer-обработчик работает и для touch, и для мыши,
    // не теряя событий (mouse leave во время drag, например).
    // Approved записи заблокированы даже при обычном tap — они уже ушли в payroll.
    // Rejected — НЕ блокируем, short-press откроет correction modal (в TimesheetPage).
    const effectiveReadOnly = isReadOnly || workflowStatus === 'approved';

    const handlePointerDown = useCallback((e) => {
        if (effectiveReadOnly) return;
        // Игнорируем правую кнопку мыши (context menu отключён)
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isLongPress.current = false;
        pressStartPos.current = { x: e.clientX, y: e.clientY };
        cancelTimer();
        pressTimer.current = setTimeout(() => {
            isLongPress.current = true;
            onLongPress?.(employeeId, jobId);
        }, LONG_PRESS_DURATION);
    }, [employeeId, jobId, effectiveReadOnly, onLongPress, cancelTimer]);

    const handlePointerMove = useCallback((e) => {
        if (!pressStartPos.current) return;
        const dx = e.clientX - pressStartPos.current.x;
        const dy = e.clientY - pressStartPos.current.y;
        if (dx * dx + dy * dy > LONG_PRESS_MOVE_THRESHOLD * LONG_PRESS_MOVE_THRESHOLD) {
            cancelTimer();
            pressStartPos.current = null;
        }
    }, [cancelTimer]);

    const handlePointerUp = useCallback((e) => {
        const hadTimer = pressTimer.current !== null;
        cancelTimer();
        if (!isLongPress.current && !effectiveReadOnly && pressStartPos.current && hadTimer) {
            onShortPress?.(employeeId, jobId, employeeIndex, jobIndex);
        }
        pressStartPos.current = null;
    }, [employeeId, jobId, employeeIndex, jobIndex, effectiveReadOnly, onShortPress, cancelTimer]);

    const handlePointerCancel = useCallback(() => {
        cancelTimer();
        pressStartPos.current = null;
    }, [cancelTimer]);

    const attendShort = attendCode ? shortOf(attendCode) : '';
    const isNightCode = attendCode === NIGHT_CODE;
    const attendBg = isNightCode ? '#312e81' : '#dc2626';
    // Absence day: часов нет, но стоит absence-код — вместо '-' рисуем пилюлю кода.
    const showsAttendOnly = editingValue === undefined && hours <= 0 && !!attendShort;
    const displayValue = editingValue !== undefined
        ? (editingValue || '0')
        : (hours > 0 ? hours : '-');
    const isPending = syncStatus === 'pending';
    const isSyncing = syncStatus === 'syncing';
    const isError = syncStatus === 'error';
    const isConflict = syncStatus === 'conflict';
    // Клетка считается синхронизированной только если sync-сервис реально подтвердил.
    // Активной клетке статус тоже виден: ввод сохраняется на каждое нажатие, и
    // форман должен видеть, ушли ли часы, не уходя с клетки.
    const isSynced = syncStatus === 'synced'
        && (editingValue !== undefined ? parseFloat(editingValue) > 0 : hours > 0);

    // Admin rejected this record. Shown with red background + corner stripe.
    const isRejected = workflowStatus === 'rejected';
    // Admin approved for payroll — visually locked, violet, cannot be edited.
    const isApproved = workflowStatus === 'approved';
    // Foreman signed — green, editable but warns about re-signing.
    const isSigned = workflowStatus === 'signed' && !isRejected && !isApproved;

    const cellTitle = isRejected
        ? `Rejected${rejectedByName ? ' by ' + rejectedByName : ''}: ${rejectionReason || '(no reason given)'}`
        : isApproved
            ? 'Approved for payroll by admin — locked. Contact admin if a correction is needed.'
            : (attendCode ? `Attendance: ${attendCode}` : undefined);

    return (
        <Box
            component="td"
            id={`cell-${employeeId}-${jobId}`}
            role="gridcell"
            aria-rowindex={employeeIndex + 2}
            aria-colindex={jobIndex + 2}
            aria-readonly={effectiveReadOnly || undefined}
            aria-selected={isActive || undefined}
            aria-label={`${hours > 0 ? hours + ' hours' : 'no entry'}${attendCode ? ', attendance: ' + attendCode : ''}${hasNotes ? ', has note' : ''}${isRejected ? ', rejected by admin' : ''}${isApproved ? ', approved for payroll — locked' : ''}${isSigned ? ', signed' : ''}`}
            title={cellTitle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
            onContextMenu={(e) => e.preventDefault()}
            sx={{
                width: cellSize,
                minWidth: cellSize,
                height: 36,
                minHeight: 36,
                p: 0,
                textAlign: 'center',
                cursor: effectiveReadOnly ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                touchAction: 'manipulation',
                position: 'relative',
                // У последней колонки работ правой линии нет: рядом стоит колонка
                // Notes со своим тёмным разделителем слева — вместе они давали
                // двойную границу.
                borderRight: isLastCol ? 'none' : '1px solid #d0d0d0',
                borderBottom: '1px solid #d0d0d0',
                // Background color by state:
                //   approved  — violet (payroll lock, editable nowhere)
                //   rejected  — red (admin sent back for correction)
                //   signed    — light green (foreman signed, editable but warns)
                //   остальные sync-статусы как были
                // Активная клетка цвет статуса НЕ перекрывает (синяя заливка
                // прятала статус и значки) — её выделяет рамка ниже; голубой
                // фон только у клетки без статуса.
                bgcolor: isApproved ? '#ede9fe'   // violet — admin approved for ADP
                    : isRejected ? '#fecaca'   // красный — админ reject
                    : isError ? '#fee2e2'
                    : isConflict ? '#fde68a'
                    : isSyncing ? '#e0f2fe'
                    : isPending ? '#fef3c7'
                    : isSigned ? '#dcfce7'     // signed — лёгкий зелёный
                    : isSynced ? '#f0fdf4'
                    : isActive ? '#eff6ff'
                    : 'transparent',
                // Левая полоска — быстрый визуальный сигнал для workflow-статусов
                borderLeft: isApproved
                    ? '3px solid #7c3aed'
                    : isRejected
                        ? '3px solid #dc2626'
                        : isSigned
                            ? '3px solid #16a34a'
                            : undefined,
                // Cell has notes: triangle top-right corner (dark amber, visible on any bg)
                '&::after': hasNotes ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '0 8px 8px 0',
                    borderColor: 'transparent #b45309 transparent transparent',
                    zIndex: 1,
                } : {},
                // Status corner marker (bottom-left):
                //   approved → violet lock icon       (payroll-locked)
                //   rejected → red ! circle           (attention needed)
                //   signed   → green ✓ circle         (confirmed by foreman)
                '&::before': isApproved ? {
                    content: '"\\1F512"',              // 🔒 lock
                    position: 'absolute',
                    bottom: 0,
                    left: 1,
                    width: 12,
                    height: 12,
                    lineHeight: '12px',
                    fontSize: '10px',
                    textAlign: 'center',
                    zIndex: 1,
                    filter: 'grayscale(0.3)',
                } : isRejected ? {
                    content: '"!"',
                    position: 'absolute',
                    bottom: 1,
                    left: 2,
                    width: 12,
                    height: 12,
                    lineHeight: '12px',
                    fontSize: '10px',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: '#dc2626',
                    borderRadius: '50%',
                    textAlign: 'center',
                    zIndex: 1,
                } : isSigned ? {
                    content: '"\\2713"',   // ✓ checkmark
                    position: 'absolute',
                    bottom: 1,
                    left: 2,
                    width: 12,
                    height: 12,
                    lineHeight: '12px',
                    fontSize: '9px',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: '#16a34a',
                    borderRadius: '50%',
                    textAlign: 'center',
                    zIndex: 1,
                } : {},
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: cellSize <= 36 ? '0.75rem' : cellSize <= 44 ? '0.85rem' : '0.95rem',
                    fontWeight: (displayValue && displayValue !== '-' && displayValue !== '0') ? 600 : 400,
                    color: (displayValue && displayValue !== '-') ? 'text.primary' : 'text.disabled',
                    boxShadow: isActive ? 'inset 0 0 0 3px #1976d2' : 'none',
                    borderRadius: 0,
                    transition: 'background-color 0.1s',
                    '&:hover': {
                        bgcolor: isReadOnly || isActive ? 'transparent' : 'action.hover',
                    },
                }}
            >
                {showsAttendOnly ? (
                    /* День без часов, но с кодом: пилюля (красная — absence, индиго — ночь). */
                    <Box sx={{
                        px: 0.6, py: '1px', borderRadius: '4px',
                        bgcolor: attendBg, color: '#fff',
                        fontSize: cellSize <= 36 ? '0.58rem' : '0.65rem',
                        fontWeight: 700, lineHeight: 1.5, letterSpacing: '0.03em',
                    }}>
                        {attendShort}
                    </Box>
                ) : (displayValue || '')}
                {/* Часы + код одновременно: мини-бейдж кода в правом-нижнем углу
                    (верх-право занят маркером заметки, низ-лево — статусом).
                    Во время ввода тоже: иначе NW у активной клетки пропадал. */}
                {attendShort && (hours > 0 || editingValue !== undefined) && (
                    <Box sx={{
                        position: 'absolute', bottom: 0, right: 0,
                        px: '3px', fontSize: '8px', lineHeight: '11px', fontWeight: 700,
                        color: '#fff', bgcolor: attendBg, borderTopLeftRadius: '4px',
                        zIndex: 1, pointerEvents: 'none',
                    }}>
                        {attendShort}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// Клеток в таблице может быть 1000+. Без memo каждое изменение state
// в TimesheetPage перерендеривает ВСЕ клетки. Custom comparator
// сравнивает только props, влияющие на визуал.
function areEqual(prev, next) {
    return prev.hours === next.hours
        && prev.editingValue === next.editingValue
        && prev.syncStatus === next.syncStatus
        && prev.workflowStatus === next.workflowStatus
        && prev.rejectionReason === next.rejectionReason
        && prev.rejectedByName === next.rejectedByName
        && prev.hasNotes === next.hasNotes
        && prev.attendCode === next.attendCode
        && prev.isCompact === next.isCompact
        && prev.cellSize === next.cellSize
        && prev.isActive === next.isActive
        && prev.isReadOnly === next.isReadOnly
        && prev.employeeId === next.employeeId
        && prev.jobId === next.jobId
        && prev.employeeIndex === next.employeeIndex
        && prev.jobIndex === next.jobIndex
        && prev.onShortPress === next.onShortPress
        && prev.onLongPress === next.onLongPress;
}

export default React.memo(TimesheetCell, areEqual);
